import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import { FilterValues } from "@models/IFilterConfig";
import ICategory from "@models/ICategory";
import FeedbackService, { FEEDBACK_SOURCE_FILTER_BAR } from "@services/FeedbackService";
import { toQueryStringParts } from "@utils/apiQueryString";
import { useFilterPresets } from "@hooks/shared/useFilterPresets";
import { useCategoryResolver } from "@hooks/shared/useSocketLookups";
import { buildInventoryFilterSections, IInventoryFilterQuery } from "@config/filters/inventoryFilters";
import FilterBar from "../../../shared/FilterBar/FilterBar";
import { useUrlFilters } from "../../../shared/FilterBar/useUrlFilters";

export const INVENTORY_FILTER_ACTIONS_ID = "inventory-filter-actions";

/** The Inventory URL carries only filters. */
const NON_FILTER_PARAMS: string[] = [];

type Props = {
    /** Root categories of the selected catalog, for the drill-down category picker */
    categories: ICategory[];
    /** A price group is selected, so the price range filters apply */
    hasPriceGroup: boolean;
    /** Baseline filters (the supplier's out-of-stock-only setting) */
    defaultFilters: FilterValues;
    isSearching: boolean;
    /** Existing Inventory fetch: receives the query keyed by `getStockLevels` field names */
    searchFn: (query: IInventoryFilterQuery) => void;
};

/**
 * Inventory (standard view) integration of the shared FilterBar (v2 search UI).
 * Screen key is ScreenOptionsEnum.INVENTORY ("inventory"), which is also the presets JSON key.
 * `getStockLevels` takes typed socket fields rather than a queryString, so the query object is handed
 * to the screen as is; the URL still mirrors it for deep links and Back / Forward.
 */
export const InventoryFilterBar = (props: Props) => {
    const { categories, hasPriceGroup, defaultFilters, searchFn } = props;
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();
    const navigate = useNavigate();
    const categoryLookup = useCategoryResolver();

    const formatMessage = useCallback(
        (id: string) => (intl.messages && intl.messages[id] !== undefined ? intl.formatMessage({ id }) : id),
        [intl]
    );

    const sections = useMemo(
        () => buildInventoryFilterSections({ hasPriceGroup, categories, lookups: categoryLookup, formatMessage }),
        [hasPriceGroup, categories, categoryLookup, formatMessage]
    );

    // Deep links (e.g. /inventory?stock=out_of_stock_only): read on mount and again on Back / Forward.
    const { initialFilters, historyKey, isFirstSearch } = useUrlFilters(sections, NON_FILTER_PARAMS);

    const { presets, onPresetsChange } = useFilterPresets(ScreenOptionsEnum.INVENTORY);

    const handleSearch = useCallback(
        (query: { [apiKey: string]: unknown }) => {
            const parts = toQueryStringParts(query);

            // Keep the URL in sync so filtered views can be shared / deep-linked.
            // The mount search replaces (no extra history entry); user searches push.
            navigate({ pathname: "/inventory", search: parts.length ? `?${parts.join("&")}` : "" }, { replace: isFirstSearch.current });
            isFirstSearch.current = false;

            searchFn(query as IInventoryFilterQuery);
        },
        [navigate, searchFn, isFirstSearch]
    );

    const handleFeedback = useCallback(
        async (message: string) => {
            try {
                await FeedbackService.sendFeedback(message, FEEDBACK_SOURCE_FILTER_BAR, { userModel, supplierModel, vendorModel });
                uiContext.notifyFn(null, { message: "feedback_sent", level: NotificationLevelEnum.success });
            } catch (error) {
                console.error("Failed to send FilterBar feedback", error);
                uiContext.notifyFn(null, { message: "feedback_failed", level: NotificationLevelEnum.error });
                throw error;
            }
        },
        [userModel, supplierModel, vendorModel, uiContext]
    );

    if (presets === null) {
        return null;
    }

    return (
        <FilterBar
            key={historyKey}
            sections={sections}
            presets={presets}
            onSearch={handleSearch}
            onPresetsChange={onPresetsChange}
            screenKey={ScreenOptionsEnum.INVENTORY}
            mode={"drawer"}
            defaultFilters={defaultFilters}
            initialFilters={initialFilters}
            allLabel={"all_inventory"}
            actionsContainerId={INVENTORY_FILTER_ACTIONS_ID}
            isSearching={props.isSearching}
            onSendFeedback={handleFeedback}
        />
    );
};

export default InventoryFilterBar;
